import * as Sentry from '@sentry/react';
import { TextInput, useToast } from '@televet/kibble-ui';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { TaskRepeatIntervalJsonType } from '@televet/shared-types/JsonTypes/TaskRepeatIntervalJsonType';
import { AvailableFrequencies, TaskWithMemoizedValues } from 'pages/Tasks/types';
import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  TaskStatus,
  TaskType,
  useCreateOneTaskMutation,
  useDeleteOneTaskMutation,
  useUpdateOneTaskMutation,
} from 'shared/types/graphql';
import AssigneesSelector from './AssigneesSelector';
import { PetParentSearch } from './components/PetParentSearch';
import RepeatIntervalSelect from './RepeatIntervalSelect';
import RepeatWeeklyDaySelect from './RepeatWeeklyDaySelect';

interface CustomTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAction: () => void;
  existingTask?: TaskWithMemoizedValues;
}

interface CustomTaskFormData {
  name: string;
  description?: string;
  repeatInterval: AvailableFrequencies;
  repeatIntervalSelectedDay?: number;
  selectedPetParentId?: string;
  selectedAssigneeIds: string[];
}

const CustomTaskModal = ({ isOpen, onClose, onAction, existingTask }: CustomTaskModalProps): JSX.Element => {
  const toast = useToast();
  const { currentClinicId } = useClinicUser();
  const { trackEvent } = useTracking();
  const [createTask, { loading: isCreateLoading }] = useCreateOneTaskMutation();
  const [updateTask, { loading: isUpdateLoading }] = useUpdateOneTaskMutation();
  const [deleteTask, { loading: isDeleteLoading }] = useDeleteOneTaskMutation();

  // Form fields
  const { control, errors, register, handleSubmit, getValues, setValue, watch, clearError } =
    useForm<CustomTaskFormData>({
      mode: 'onBlur',
      reValidateMode: 'onChange',
      validateCriteriaMode: 'all',
      defaultValues: {
        name: existingTask?.name || '',
        description: existingTask?.description || '',
        repeatInterval: existingTask?.repeatIntervalDefinition?.frequency || 'Does not repeat',
        repeatIntervalSelectedDay: existingTask?.repeatIntervalDefinition?.dayOfWeek,
        selectedPetParentId: existingTask?.clinicPetParent?.id,
        selectedAssigneeIds: existingTask?.assignees.map((a) => a.id) || [],
      },
    });

  const repeatInterval = watch('repeatInterval');
  const repeatIntervalSelectedDay = watch('repeatIntervalSelectedDay');
  const selectedPetParentId = watch('selectedPetParentId');
  const selectedAssigneeIds = watch('selectedAssigneeIds');

  const getEventData = useCallback(() => {
    const values = getValues() as unknown as CustomTaskFormData;
    return {
      clinicId: currentClinicId,
      taskId: existingTask?.id || '',
      ...values,
      selectedAssigneeIds: values?.selectedAssigneeIds?.join(', '),
    };
  }, [currentClinicId, existingTask?.id, getValues]);

  const setRepeatInterval = useCallback(
    (interval: AvailableFrequencies): void => {
      setValue('repeatInterval', interval);
      if (repeatInterval !== 'Weekly') {
        setValue('repeatIntervalSelectedDay', undefined);
        clearError('repeatIntervalSelectedDay');
      }
    },
    [clearError, repeatInterval, setValue],
  );

  const setRepeatIntervalSelectedDay = useCallback(
    (day: number | undefined): void => {
      setValue('repeatIntervalSelectedDay', day);
    },
    [setValue],
  );

  const setSelectedPetParentId = useCallback(
    (id: string | undefined): void => {
      setValue('selectedPetParentId', id);
    },
    [setValue],
  );

  const setSelectedAssigneeIds = useCallback(
    (ids: string[]): void => {
      setValue('selectedAssigneeIds', ids);
    },
    [setValue],
  );

  const onSubmit = useCallback(
    async ({
      name,
      description,
      repeatInterval,
      repeatIntervalSelectedDay,
      selectedPetParentId,
      selectedAssigneeIds,
    }: CustomTaskFormData): Promise<void> => {
      try {
        const repeatIntervalDefinition: TaskRepeatIntervalJsonType | Record<string, never> =
          !repeatInterval || repeatInterval === 'Does not repeat'
            ? {}
            : {
                frequency: repeatInterval,
                dayOfWeek: repeatIntervalSelectedDay,
                originallyCreatedAt:
                  existingTask?.repeatIntervalDefinition?.originallyCreatedAt || new Date().toISOString(),
                timesCompleted: existingTask?.repeatIntervalDefinition?.timesCompleted || 0,
              };

        if (existingTask) {
          await updateTask({
            variables: {
              data: {
                name,
                description,
                repeatIntervalDefinition,
                assignees: {
                  disconnect: existingTask.assignees
                    .filter((a) => !selectedAssigneeIds.includes(a.id))
                    .map((a) => ({ id: a.id })),
                  connect: selectedAssigneeIds
                    .filter((id) => !existingTask.assignees.some((a) => a.id === id))
                    .map((id) => ({ id })),
                },
                type: TaskType.Custom,
                clinicPetParent:
                  existingTask.clinicPetParent?.id && !selectedPetParentId
                    ? {
                        disconnect: true,
                      }
                    : {
                        connect: selectedPetParentId ? { id: selectedPetParentId } : undefined,
                      },
              },
              where: {
                id: existingTask.id,
              },
            },
          });
          trackEvent(TasksEvents.CustomTaskEdited, getEventData());
        } else {
          await createTask({
            variables: {
              data: {
                name,
                description,
                repeatIntervalDefinition,
                assignees: {
                  connect: selectedAssigneeIds.map((id) => ({ id })),
                },
                clinicPetParent: selectedPetParentId ? { connect: { id: selectedPetParentId } } : undefined,
                clinic: {
                  connect: { id: currentClinicId || '' },
                },
                type: TaskType.Custom,
                status: TaskStatus.Open,
              },
            },
          });
          trackEvent(TasksEvents.CustomTaskCreated, getEventData());
        }
        onAction();
        onClose();
      } catch (e) {
        Sentry.captureException(e);
        toast({
          status: 'error',
          description: 'Oops, an error occurred while saving the task list.',
        });
        console.error(e);
      }
    },
    [existingTask, onAction, onClose, updateTask, trackEvent, getEventData, createTask, currentClinicId, toast],
  );

  const deleteExistingTask = useCallback(async (): Promise<void> => {
    if (!existingTask) return;
    try {
      await deleteTask({
        variables: {
          where: {
            id: existingTask.id,
          },
        },
      });
      trackEvent(TasksEvents.CustomTaskDeleted, getEventData());
      onAction();
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        description: 'Oops, an error occurred while deleting the task.',
      });
      console.error(e);
    }
  }, [deleteTask, existingTask, getEventData, onAction, onClose, toast, trackEvent]);

  const onPetParentDelete = useCallback((): void => {
    setValue('selectedPetParentId', '');
  }, [setValue]);

  return (
    <Modal onClose={onClose} isOpen={isOpen} size="sm" isCentered scrollBehavior="inside" allowEscape={true}>
      <ModalHeader>{existingTask ? 'Edit Task' : 'New Task'}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Flex gap={5} direction="column">
          <TextInput
            name="name"
            placeholder="Task Name"
            isInvalid={!!errors.name}
            errorText="Please enter a name"
            label="Task Name"
            data-testid="custom-task-modal-name-input"
            isRequired={true}
            ref={register({ required: { value: true, message: 'Please enter a name' } })}
            autoFocus={!existingTask}
          />
          <Textarea
            size="md"
            minH={20}
            placeholder="Add task details..."
            name="description"
            ref={register}
            label="Task Details"
            data-testid="custom-task-modal-details-textarea"
          />
          <Controller
            as={RepeatIntervalSelect}
            control={control}
            name="repeatInterval"
            repeatInterval={repeatInterval}
            setRepeatInterval={setRepeatInterval}
          />
          {repeatInterval === 'Weekly' && (
            <Controller
              as={RepeatWeeklyDaySelect}
              control={control}
              name="repeatIntervalSelectedDay"
              repeatIntervalSelectedDay={repeatIntervalSelectedDay}
              setRepeatIntervalSelectedDay={setRepeatIntervalSelectedDay}
              isWeeklyDayInvalid={
                repeatIntervalSelectedDay === undefined &&
                repeatInterval === 'Weekly' &&
                !!errors.repeatIntervalSelectedDay
              }
              rules={{
                required: {
                  value: true,
                  message: 'Please select a day of the week',
                },
              }}
            />
          )}
          <Controller
            as={PetParentSearch}
            control={control}
            name="selectedPetParentId"
            selectedPetParentId={selectedPetParentId}
            onSelect={setSelectedPetParentId}
            onDelete={onPetParentDelete}
          />
          <Box w="100%">
            <Controller
              control={control}
              name="selectedAssigneeIds"
              as={AssigneesSelector}
              selectedAssigneeIds={selectedAssigneeIds}
              setSelectedAssigneeIds={setSelectedAssigneeIds}
            />
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter justifyContent="space-between">
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Flex>
          {existingTask && (
            <Button variant="primaryDestructive" mr={3} isLoading={isDeleteLoading} onClick={deleteExistingTask}>
              Delete Task
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            isDisabled={isDeleteLoading}
            isLoading={isCreateLoading || isUpdateLoading}
          >
            Save
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default CustomTaskModal;
